import { useEffect, useRef } from 'react';

/**
 * Single-key and two-key ("g d") keyboard shortcuts for the desk portal.
 *
 * Deliberately modifier-free, like the mail clients officers already use, so
 * they are suppressed while the focus is anywhere text can be typed — an
 * officer writing "need access" in a note must not be sent to the dashboard by
 * the "g". Cmd/Ctrl/Alt chords pass through untouched (Cmd-K belongs to the
 * palette's own hook).
 */

export type ShortcutId =
  | 'help'
  | 'palette'
  | 'goDashboard'
  | 'goMap'
  | 'newFile'
  | 'fileVisit'
  | 'fileDocuments'
  | 'fileAssign'
  | 'fileEdit';

export interface ShortcutDef {
  id: ShortcutId;
  /** Keys pressed in sequence, e.g. `['g', 'd']`. */
  keys: readonly string[];
  /** Only meaningful while a file is open. */
  fileOnly?: boolean;
}

/** The catalogue, in the order the help overlay lists it. */
export const SHORTCUTS: readonly ShortcutDef[] = [
  { id: 'help', keys: ['?'] },
  { id: 'palette', keys: ['/'] },
  { id: 'goDashboard', keys: ['g', 'd'] },
  { id: 'goMap', keys: ['g', 'm'] },
  { id: 'newFile', keys: ['n'] },
  { id: 'fileVisit', keys: ['v'], fileOnly: true },
  { id: 'fileDocuments', keys: ['d'], fileOnly: true },
  { id: 'fileAssign', keys: ['a'], fileOnly: true },
  { id: 'fileEdit', keys: ['e'], fileOnly: true },
];

/** How long the second key of a sequence may follow the first. */
export const SEQUENCE_TIMEOUT_MS = 1200;

export function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== 'string') return false;
  const el = target as HTMLElement;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(type);
  }
  // A combobox or textbox role that is not a native field (MUI Select's div).
  const role = el.getAttribute('role');
  return role === 'textbox' || role === 'combobox';
}

export type ShortcutHandlers = Partial<Record<ShortcutId, () => void>>;

/**
 * Listen for the catalogue's shortcuts and run whichever handler is present.
 * A shortcut with no handler is inert — the caller leaves out what does not
 * apply (no file open, no authority to create one).
 */
export function useKeyboardShortcuts(handlers: ShortcutHandlers, enabled = true): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) return;
    let pending: string | null = null;
    let pendingAt = 0;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      // An open dialog owns the keyboard (the palette, a confirm, a form).
      const active = document.activeElement as HTMLElement | null;
      if (active && active.closest('[role="dialog"]')) return;
      const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      if (key === '?' || (event.key === '/' && event.shiftKey)) {
        const run = handlersRef.current.help;
        if (run) {
          event.preventDefault();
          run();
        }
        pending = null;
        return;
      }

      const now = Date.now();
      const prefix = pending && now - pendingAt <= SEQUENCE_TIMEOUT_MS ? pending : null;
      pending = null;

      if (prefix) {
        const match = SHORTCUTS.find(
          (s) => s.keys.length === 2 && s.keys[0] === prefix && s.keys[1] === key,
        );
        const run = match && handlersRef.current[match.id];
        if (run) {
          event.preventDefault();
          run();
          return;
        }
      }

      if (SHORTCUTS.some((s) => s.keys.length === 2 && s.keys[0] === key)) {
        pending = key;
        pendingAt = now;
        return;
      }

      const single = SHORTCUTS.find((s) => s.keys.length === 1 && s.keys[0] === key);
      const run = single && handlersRef.current[single.id];
      if (run) {
        event.preventDefault();
        run();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}

/** The file id in the current path, when a file (not the intake form) is open. */
export function fileIdFromPath(pathname: string): string | null {
  const match = /^\/files\/([^/?#]+)/.exec(pathname);
  if (!match || match[1] === 'new') return null;
  return decodeURIComponent(match[1]);
}
